import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { injectIntl, intlShape } from 'react-intl';
import { Col, Row } from 'reactstrap';

import globalTranslations from '../../../../i18n/globalTranslations';
import { createForm } from '../../../../models/Form';
import { createFormField } from '../../../../models/FormField';
import OrderCheckoutProduct from '../../../../models/order/checkout/OrderCheckoutProduct';
import { modelOf } from '../../../../prop-types';
import CheckoutStore from '../../../../store/CheckoutStore';
import UIStore from '../../../../store/UIStore';
import { getReferenceFieldValidators } from '../../../../util/checkoutFormUtils';
import { copyToClipboard } from '../../../../util/navigator';
import {
  productCountValid,
  quantityValid,
} from '../../../../util/formValidators';
import CheckoutCartProductDetails from '../product/CheckoutCartProductDetails';
import CheckoutCartProductReference from '../product/CheckoutCartProductReference';
import CheckoutCartProductImage from '../product/CheckoutCartProductImage';
import CheckoutCartProductPrice, {
  PART_TOTAL,
  PART_UNIT,
} from '../product/CheckoutCartProductPrice';
import CheckoutCartProductQuantity from '../product/CheckoutCartProductQuantity';
import CheckoutCartProductRemove from '../product/CheckoutCartProductRemove';

const CheckoutCartProductRowB2B = ({
  checkoutStore,
  uiStore,
  product,
  onOrderUpdate,
  disabled,
  showProductCode,
  intl,
}) => {
  const { cancelOrder } = checkoutStore;
  const quantityFieldName = `checkoutCartProductRowB2B_quantity_${product.id}`;
  const referenceFieldName = `checkoutCartProductRowB2B_reference_${product.id}`;
  const [productCodeCopied, setProductCodeCopied] = useState(false);
  const copyResetTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        window.clearTimeout(copyResetTimeoutRef.current);
      }
    };
  }, []);

  const form = useMemo(() => {
    const step = product.getQuantityStep();
    const max = 99999;
    const referenceField = product.reference_field;
    const referenceValidators = getReferenceFieldValidators(
      referenceField,
      intl.formatMessage(globalTranslations.referenceTitle)
    );

    const formFields = {
      [quantityFieldName]: createFormField(
        {
          name: quantityFieldName,
          value: product.quantity * step,
        },
        [quantityValid(max, step), productCountValid(step)]
      ),
      [referenceFieldName]: createFormField(
        {
          name: referenceFieldName,
          value: referenceField?.value || null,
          type: referenceField?.type,
          required: referenceField?.required ?? false,
        },
        referenceValidators,
        () => referenceField
      ),
    };
    return createForm(formFields);
  }, [product.id, product.quantity]);

  const updateOrder = (args) => onOrderUpdate(args);

  const removeProduct = () => {
    product.updateQuantity(0);
    updateOrder({ withoutTimer: true });
  };

  const onCopyProductCode = useCallback(async () => {
    if (!product.actual_code) return;
    const ok = await copyToClipboard(product.actual_code);
    if (!ok) return;
    setProductCodeCopied(true);
    if (copyResetTimeoutRef.current) {
      window.clearTimeout(copyResetTimeoutRef.current);
    }
    copyResetTimeoutRef.current = window.setTimeout(() => {
      setProductCodeCopied(false);
    }, 1200);
  }, [product.actual_code]);

  const countProductQuantity = (value) => value / product.getQuantityStep();

  const resetProductQuantity = () => {
    const quantityField = form.getField(quantityFieldName);
    if (quantityField.value !== '') return;
    quantityField.setValue(
      String(product.quantity * product.getQuantityStep())
    );
  };

  const updateProductQuantity = (value) => {
    const quantityField = form.getField(quantityFieldName);
    if (quantityField.value === '') {
      cancelOrder();
      return;
    }
    const newQuantity = countProductQuantity(quantityField.value);
    form.validateField(quantityFieldName);
    if (quantityField.valid) {
      product.updateQuantity(newQuantity);
      updateOrder({ customTimer: 800 });
    }
  };

  const referenceField = form.getField(referenceFieldName);
  const quantityField = form.getField(quantityFieldName);

  // Desktop: three columns — image | details + reference | quantity + price + remove (icon)
  const renderDesktopRow = () => (
    <>
      <Col xl={1} className="CheckoutCartProductRowB2B__col-image">
        <CheckoutCartProductImage product={product} />
      </Col>
      <Col xl={5} className="CheckoutCartProductRowB2B__col-details">
        <CheckoutCartProductDetails
          product={product}
          unitPriceContent={null}
          showProductCode={showProductCode}
          productCodeCopied={productCodeCopied}
          onCopyProductCode={onCopyProductCode}
          showAvailability
        />
      </Col>
      <Col xl={6} className="CheckoutCartProductRowB2B__col-quantity-price">
        <div className="CheckoutCartProductRowB2B__quantity-price-group">
          <CheckoutCartProductQuantity
            product={product}
            quantityField={quantityField}
            disabled={disabled}
            onUpdateProductQuantity={updateProductQuantity}
            onBlur={resetProductQuantity}
            onFocus={cancelOrder}
            customError={product.quantity_error}
          />
          <span
            className="CheckoutCartProductRowB2B__price-sep"
            aria-hidden="true"
          >
            ×
          </span>
          <div className="CheckoutCartProductRowB2B__unit-price-wrap">
            <CheckoutCartProductPrice product={product} part={PART_UNIT} />
          </div>
          <span
            className="CheckoutCartProductRowB2B__price-sep"
            aria-hidden="true"
          >
            =
          </span>
          <div className="CheckoutCartProductRowB2B__total-price-wrap">
            <CheckoutCartProductPrice product={product} part={PART_TOTAL} />
          </div>
          <CheckoutCartProductRemove
            onRemove={removeProduct}
            disabled={disabled}
            ariaLabel={intl.formatMessage(globalTranslations.removeTitle)}
            isIconOnly
          />
        </div>
        <CheckoutCartProductReference
          referenceField={referenceField}
          product={product}
          onOrderUpdate={updateOrder}
        />
      </Col>
    </>
  );

  const renderMobileRow = () => (
    <>
      <Col xs={12} className="CheckoutCartProductRowB2B__col">
        <div className="CheckoutCartProductRowB2B__product--mobile">
          <div className="CheckoutCartProductRowB2B__product-head--mobile">
            <CheckoutCartProductImage product={product} />
            <div className="CheckoutCartProductRowB2B__product-head-content--mobile">
              <CheckoutCartProductDetails
                product={product}
                unitPriceContent={null}
                showProductCode={false}
                onlyNameAndUnitPrice
              />
              <CheckoutCartProductPrice product={product} />
            </div>
          </div>
          <div className="CheckoutCartProductRowB2B__product-utility-actions--mobile">
            <CheckoutCartProductQuantity
              product={product}
              quantityField={quantityField}
              disabled={disabled}
              onUpdateProductQuantity={updateProductQuantity}
              onBlur={resetProductQuantity}
              onFocus={cancelOrder}
              customError={product.quantity_error}
            />
            <CheckoutCartProductRemove
              onRemove={removeProduct}
              disabled={disabled}
              ariaLabel={intl.formatMessage(globalTranslations.removeTitle)}
            />
          </div>
          <CheckoutCartProductDetails
            product={product}
            unitPriceContent={null}
            showProductCode={showProductCode}
            productCodeCopied={productCodeCopied}
            onCopyProductCode={onCopyProductCode}
            showAvailability
            hideNameAndUnitPrice
          />
          <CheckoutCartProductReference
            referenceField={referenceField}
            product={product}
            onOrderUpdate={updateOrder}
          />
        </div>
      </Col>
    </>
  );

  const content = !uiStore.isLargest ? renderMobileRow() : renderDesktopRow();

  return <Row className="CheckoutCartProductRowB2B">{content}</Row>;
};

CheckoutCartProductRowB2B.propTypes = {
  checkoutStore: modelOf(CheckoutStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  product: modelOf(OrderCheckoutProduct).isRequired,
  intl: intlShape.isRequired,
  onOrderUpdate: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  showProductCode: PropTypes.bool,
};

export default injectIntl(
  inject('checkoutStore', 'uiStore')(observer(CheckoutCartProductRowB2B))
);
